// HeroScreen.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import "./HeroScreen.css";

export default function HeroScreen({ profile }) {
  const lines = useMemo(() => {
    const role = profile?.role || "Software Engineer";
    const location = profile?.location || "Sri Lanka";
    const stack = profile?.stack || ["React", "Node", "Firebase", "Flutter"];
    const links = profile?.links || [];

    const safeTagline = profile?.tagline || "Building useful things, daily.";

    const linkLines = [
      `  links: [`,
      ...links.map((l) => `    "${l.label}",`),
      `  ],`,
    ];

    return [
      `const astro = {`,
      `  role: "${role}",`,
      `  stack: ${JSON.stringify(stack)},`,
      `  location: "${location}",`,
      ...linkLines,
      `}`,
      ``,
      `$ echo "${safeTagline}"`,
      `${safeTagline}`,
      `$ `,
    ];
  }, [profile]);

  // typing settings
  const TYPE_MS = 18;
  const LINE_PAUSE_MS = 260;
  const START_DELAY_MS = 350;

  const [typedLines, setTypedLines] = useState([""]);
  const [isTyping, setIsTyping] = useState(true);

  const lineIndexRef = useRef(0);
  const charIndexRef = useRef(0);
  const timerRef = useRef(null);

  const resetTyping = () => {
    lineIndexRef.current = 0;
    charIndexRef.current = 0;
    setTypedLines([""]);
    setIsTyping(true);
  };

  useEffect(() => {
    resetTyping();

    const tick = () => {
      const li = lineIndexRef.current;
      const ci = charIndexRef.current;

      if (li >= lines.length) {
        setIsTyping(false);
        return;
      }

      const full = lines[li];

      setTypedLines((prev) => {
        const copy = [...prev];
        while (copy.length <= li) copy.push("");
        return copy;
      });

      if (ci < full.length) {
        setTypedLines((prev) => {
          const copy = [...prev];
          copy[li] = (copy[li] || "") + full[ci];
          return copy;
        });

        charIndexRef.current += 1;
        timerRef.current = setTimeout(tick, TYPE_MS);
        return;
      }

      lineIndexRef.current += 1;
      charIndexRef.current = 0;

      setTypedLines((prev) => {
        const copy = [...prev];
        if (copy.length <= li + 1) copy.push("");
        return copy;
      });

      timerRef.current = setTimeout(tick, LINE_PAUSE_MS);
    };

    timerRef.current = setTimeout(tick, START_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [lines]);

  // ✅ clickable link label renderer (shows only GitHub/LinkedIn, but clicks to href)
  const renderLinkLabelLine = (line, index) => {
    const m = line.match(/^(\s*")([^"]+)(".*)$/);
    if (!m) return null;

    const [, pre, label, post] = m;

    const href = (profile?.links || []).find(
      (l) => (l?.label || "").toLowerCase() === label.toLowerCase()
    )?.href;

    // if not found, render plain
    if (!href) {
      return (
        <span key={index}>
          <span className="dim">{pre}</span>
          <span className="dim">{label}</span>
          <span className="dim">{post}</span>
        </span>
      );
    }

    return (
      <span key={index}>
        <span className="dim">{pre}</span>
        <a className="codeLink" href={href} target="_blank" rel="noreferrer">
          {label}
        </a>
        <span className="dim">{post}</span>
      </span>
    );
  };

  // syntax coloring
  const renderLine = (line, index) => {
    const isPrompt = line.startsWith("$");
    const isObjStart = line.startsWith("const astro = {");
    const isClose = line.trim() === "}";
    const isKeyLine =
      line.includes("role:") ||
      line.includes("focus:") ||
      line.includes("stack:") ||
      line.includes("location:") ||
      line.includes("links:");

    if (isObjStart) {
      return (
        <span key={index}>
          {"const "}
          <span className="kw">astro</span>
          {" = {"}
        </span>
      );
    }

    if (isPrompt) {
      const rest = line.slice(1);
      return (
        <span key={index}>
          <span className="prompt">$</span>
          <span className="dim">{rest}</span>
        </span>
      );
    }

    // ✅ clickable label lines inside links array
    if (line.trim().startsWith('"') && line.trim().endsWith('",')) {
      if (line.startsWith("    ")) {
        const node = renderLinkLabelLine(line, index);
        if (node) return node;
      }
    }

    if (isKeyLine) {
      const left = line.split(":")[0];
      const right = line.slice(line.indexOf(":") + 1);
      return (
        <span key={index}>
          <span className="kw">{left}</span>
          {":"}
          <span className="dim">{right}</span>
        </span>
      );
    }

    if (isClose) return <span key={index}>{"}"}</span>;
    return <span key={index}>{line}</span>;
  };

  return (
    <section className="hero" id="top" aria-label="Hero">
      <div className="heroTerminal">
        <div className="terminal">
          <div className="terminal__top">
            <div className="terminal__dots" aria-hidden="true">
              <span className="terminal__dot terminal__dot--red" />
              <span className="terminal__dot terminal__dot--yellow" />
              <span className="terminal__dot terminal__dot--green" />
            </div>

            <div className="terminal__title">
              {(profile?.name || "user").toLowerCase()}@mac — zsh
            </div>

            <span className="terminal__spacer" aria-hidden="true" />
          </div>

          <pre className="terminal__code" aria-label="Terminal code">
            {typedLines.map((line, i) => (
              <div className="tline" key={i}>
                {renderLine(line, i)}
                {isTyping && i === typedLines.length - 1 ? (
                  <span className="cursor" aria-hidden="true" />
                ) : null}
              </div>
            ))}
          </pre>
        </div>
      </div>

      <div className={`dividerText ${isTyping ? "hidden" : "show"}`}>scroll down</div>
    </section>
  );
}
